import React, { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { searchClassForAnalytics, searchStudentForAnalytics, searchYearForAnalytics } from '../../server/server'
import { ReactComponent as SearchBlue } from '../../assets/svg/SearchBlue.svg'
import { ReactComponent as UserCardId } from '../../assets/svg/UserCardId.svg'
import InfoButton from '../common/InfoButton'
import { Link } from 'react-router-dom/cjs/react-router-dom.min'
import { useUser } from '../../auth'
import Loading from '../Loading'
import useYearAnalyticsAccess from './year/useYearAnalyticsAccess'
import { updateYearAnalyticsAccess } from '../../auth/yearAnalyticsAccessCache'

function AdvancedAnalyticsSearchWidget() {
    const { user } = useUser()
    const { t, locale } = useTranslation()
    const [searchText, setSearchText] = useState('')
    const [isFocused, setIsFocused] = useState(false)
    const [showSearchResults, setShowSearchResults] = useState(false)
    const [loading, setLoading] = useState(false)
    const [initLoading, setInitLoading] = useState(true)
    const [results, setResults] = useState([])
    const [hasMore, setHasMore] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const [total, setTotal] = useState(0)
    const [selectedButton, setSelectedButton] = useState('students') // students | classes
    const studentsSelected = selectedButton === 'students'
    const yearsSelected = selectedButton === 'years'
    const requestVersion = useRef(0)
    const inputRef = useRef(null)
    // Default (empty-search) results per tab; they don't change while the page is open.
    const emptySearchCache = useRef({})
    const [error, setError] = useState('')

    useEffect(() => {
        if (!searchText && !isFocused) {
            setLoading(false)
            return
        }
        const cached = !searchText && emptySearchCache.current[selectedButton]
        if (cached) {
            setResults(cached.results)
            setTotal(cached.total)
            setError('')
            setShowSearchResults(true)
            setLoading(false)
            return
        }
        // No keystrokes to wait for on an empty search, so skip the debounce.
        const timer = setTimeout(() => fetchResults(searchText, 0), searchText ? 400 : 0)
        return () => { clearTimeout(timer); requestVersion.current += 1 }
    }, [searchText, isFocused, selectedButton])

    const loadMore = () => {
        if (!loadingMore && !loading) {
            setLoadingMore(true)
            fetchResults(searchText, results?.length)
        }
    }

    const fetchResults = async (searchTerm, resultsNum) => {
        const version = ++requestVersion.current
        const type = selectedButton
        setLoading(true)
        setError('')
        const data = {
            searchText: searchTerm.toLowerCase(),
            loadedData: resultsNum,
        }
        const callFunction = yearsSelected ? searchYearForAnalytics : studentsSelected ? searchStudentForAnalytics : searchClassForAnalytics
        const resp = await callFunction(data)
        if (version !== requestVersion.current) return
        if (yearsSelected) updateYearAnalyticsAccess(user, resp)
        if (resp.status === 200) {
            const array = resp?.data?.results
            const size = resp?.data?.total_count?.count
            if (searchTerm === '' && resultsNum === 0) {
                emptySearchCache.current[type] = { results: array?.slice(0, 4) ?? [], total: size ?? 0 }
            }
            if (array) {
                if (resultsNum === 0) {
                    setResults(array.slice(0, 4))
                } else {
                    setResults((prev) => [...prev, ...array])
                }
                setTotal(size)
                setHasMore(size > results?.length)
            } else {
                setResults([])
                setTotal(0)
            }
            setShowSearchResults(true)
        } else {
            setResults([])
            setTotal(0)
            setShowSearchResults(true)
            setError(resp.status === 403 ? 'You do not have access to year analytics' : 'Unable to load analytics search')
        }
        setLoading(false)
        setLoadingMore(false)
    }

    const changeText = (e) => {
        setSearchText(e.target.value)
        setLoading(true)
        // setShowSearchResults(true)
        if (initLoading) {
            setInitLoading(false)
        }
    }

    // Reset here rather than in an effect: an effect on selectedButton would run after the
    // search effect in the same commit and hide results served from the cache.
    const selectTab = (tab) => {
        if (tab !== selectedButton) {
            setSelectedButton(tab)
            setSearchText('')
            setResults([])
            setTotal(0)
            setError('')
            setShowSearchResults(false)
        }
        // Focusing sets isFocused, which triggers the search for the selected tab.
        inputRef.current?.focus()
    }

    return (
        <div className=" rounded-[10px] bg-[white] px-[35px] py-[25px] ">
            <div className="flex flex-row items-center justify-between gap-[10px]">
                <p className=" text-[20px] font-bold text-[#413F5E]">{t('Search All Analytics')}</p>
                <InfoButton text={t('Use this search to explore student, class, year, or counsellor analytics and access deeper insights.')} />
            </div>

            <SearchOptionButtons selected={selectedButton} setSelected={selectTab} />
            <div
                className={`mt-[16px] flex flex-row items-center justify-center gap-[6px] border border-[#BEBECB] rounded-full h-[44px] px-[20px] bg-white relative transition-all`}
            >
                <input
                    type="text"
                    placeholder={yearsSelected ? t('Search for years') : studentsSelected ? t('Search for students') : t('Search for classes')}
                    className="flex-1 transition-all bg-[transparent] placeholder:text-[#9392AD] leading-5 text-[16px] text-deepPurple font-normal "
                    ref={inputRef}
                    value={searchText}
                    id="advanced-analytics-search-input"
                    onChange={changeText}
                    onFocus={() => {
                        setIsFocused(true)
                    }}
                    onBlur={() => {
                        setIsFocused(false)
                    }}
                />
                {loading ? <Loading primaryColor="#413F5E" /> : <SearchBlue color="#5E639B" />}
            </div>
            {showSearchResults && (
                <div className="flex flex-col gap-[16px] mt-[10px]">
                    <p className="px-[8px] text-[#413F5E] font-medium text-[14px]">
                        {loading ? t('Loading') + '...' : <span>{yearsSelected ? t('We found {{total}} year(s)', { total }) : studentsSelected ? t('We found student(s)', { total }) : t('We found class(es)', { total })}</span>}
                    </p>
                    <div className="flex flex-col gap-[8px]">
                        {error && <p role="alert" className="text-darkenPurple">{t(error)}</p>}
                        {yearsSelected && !loading && !error && total === 0 && <p className="text-strongGray">{t(searchText ? 'No matching years' : 'No year groups assigned')}</p>}
                        {results.map((e) => (
                            <Link key={e.id} to={yearsSelected ? `/analytics/year?year=${encodeURIComponent(e.id)}` : studentsSelected ? `/analytics/student?id=${e.id}` : `/analytics/class?id=${e.id}`}>
                                <div key={e.id} className="h-[40px] bg-[#F8F8FB] rounded-[8px] px-[16px] flex flex-row items-center gap-[16px]">
                                    <p className="text-[16px] text-[#413F5E] font-semibold">{e.label}</p>
                                    <p className="text-[#77768D] font-normal text-[15px]">{e.additionalInformation}</p>
                                    <div className="flex-1" />

                                    <div className="flex flex-row items-center gap-[6px]">
                                        <UserCardId />
                                        <p className="text-[#413F5E] font-semibold text-[12px]">{yearsSelected ? t('Year analytics') : studentsSelected ? t('Student analytics') : t('Class analytics')}</p>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                    {total > 4 && (
                        <div className="flex flex-row gap-[6px] items-center">
                            <Link to={`/analytics/search?search=${encodeURIComponent(searchText)}&type=${selectedButton}`}>
                                <button
                                    id="view-more-button"
                                    className="h-[40px] flex flex-row items-center justify-center bg-[#ECEBF8] rounded-full px-[20px] text-[#413F5E] font-bold text-[14px] hover:text-[white] transition-all ease-in-out hover:bg-[#9392AD]"
                                >
                                    {t('View More')}
                                </button>
                            </Link>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default AdvancedAnalyticsSearchWidget

export const SearchOptionButtons = ({ selected, setSelected }) => {
    const { t } = useTranslation()
    const { user } = useUser()
    const canAccessYear = useYearAnalyticsAccess()

    useEffect(() => {
        if (canAccessYear === false && selected === 'years') setSelected('students')
    }, [canAccessYear, selected, setSelected])

    const studentsSelected = selected === 'students'

    return (
        <div className="flex items-center w-full min-w-0 mt-[16px]">
            <div className="flex flex-nowrap gap-2 xl:gap-[10px] items-center w-full min-w-0">
                <button
                    className={`h-[36px] rounded-full flex-1 min-w-0 px-1 border text-center whitespace-nowrap font-semibold text-[12px] xl:text-[16px] transition-all ${
                        studentsSelected ? 'bg-deepPurple border-deepPurple text-white' : 'border-[#BEBECE] text-[#77768D] hover:border-deepPurple hover:text-deepPurple'
                    } `}
                    onClick={() => setSelected('students')}
                >
                    {t('Students')}
                </button>
                <button
                    className={`h-[36px] rounded-full flex-1 min-w-0 px-1 border text-center whitespace-nowrap font-semibold text-[12px] xl:text-[16px] transition-all ${
                        selected === 'classes' ? 'bg-deepPurple border-deepPurple text-white' : 'border-[#BEBECE] text-[#77768D] hover:border-deepPurple hover:text-deepPurple'
                    } `}
                    onClick={() => setSelected('classes')}
                    id="classes-analytics-button"
                >
                    {t('Classes')}
                </button>

                {canAccessYear && (
                    <button
                        id="year-analytics-button"
                        className={`h-[36px] rounded-full flex-1 min-w-0 px-1 border text-center whitespace-nowrap font-semibold text-[12px] xl:text-[16px] transition-all ${selected === 'years' ? 'bg-deepPurple border-deepPurple text-white' : 'border-[#BEBECE] text-[#77768D] hover:border-deepPurple hover:text-deepPurple'}`}
                        onClick={() => setSelected('years')}
                    >{t('Years')}</button>
                )}
                <Link
                    to={`/analytics/counsellor`}
                    className={`flex items-center justify-center h-[36px] rounded-full flex-1 min-w-0 px-1 border text-center whitespace-nowrap font-semibold text-[12px] xl:text-[16px] transition-all pb-0 mb-0 ${
                        false ? 'bg-deepPurple border-deepPurple text-white' : 'border-[#BEBECE] text-[#77768D] hover:border-deepPurple hover:text-deepPurple'
                    } `}
                    id="counsellor-analytics-button"
                >
                    <p>{t('Counsellor')}</p>
                </Link>
                {user?.allowedToAccessOrganizationAnalytics && (
                    <Link
                        to={`/analytics/organization`}
                        className={`flex items-center justify-center h-[36px] rounded-full flex-1 min-w-0 px-1 border text-center whitespace-nowrap font-semibold text-[12px] xl:text-[16px] transition-all pb-0 mb-0 ${
                            false ? 'bg-deepPurple border-deepPurple text-white' : 'border-[#BEBECE] text-[#77768D] hover:border-deepPurple hover:text-deepPurple'
                        } `}
                        id="organization-analytics-button"
                    >
                        <p>{t('Organization')}</p>
                    </Link>
                )}
            </div>
        </div>
    )
}
